
import React, { useState } from 'react';
import { Company, UserFeaturesConfig } from '../types';
import { api } from '../services/apiService';

interface UserFeaturesTabProps {
  company: Company;
  onCompanyUpdate: (updated: Company) => void;
}

interface FeatureDef {
  key: keyof UserFeaturesConfig;
  label: string;
  description: string;
  defaultOn: boolean;
  group: string;
}

const FEATURES: FeatureDef[] = [
  // Prospects
  { key: 'viewSwitch',        label: 'Changement de vue',          description: 'Permet de basculer entre les vues liste, cartes et kanban.',        defaultOn: true,  group: 'Prospects' },
  { key: 'search',            label: 'Barre de recherche',         description: 'Recherche par mot-clé dans les prospects.',                          defaultOn: true,  group: 'Prospects' },
  { key: 'tagSearch',         label: 'Recherche par tag',          description: 'Champ de recherche par étiquette (tag).',                            defaultOn: true,  group: 'Prospects' },
  { key: 'filters',           label: 'Filtres (agent / statut / secteur)', description: 'Filtres déroulants pour affiner la liste des prospects.',     defaultOn: true,  group: 'Prospects' },
  { key: 'colorFilter',       label: 'Filtre par couleur',         description: 'Pastilles de filtrage par couleur de revenu potentiel.',             defaultOn: true,  group: 'Prospects' },
  { key: 'exportCsv',         label: 'Export CSV',                 description: 'Bouton d\'export des prospects en fichier CSV.',                     defaultOn: false, group: 'Import / Export' },
  { key: 'importCsv',         label: 'Import CSV',                 description: 'Bouton d\'import de prospects depuis un fichier CSV.',               defaultOn: false, group: 'Import / Export' },
  // Dashboard
  { key: 'dashboardCustomize',label: 'Personnalisation du tableau de bord', description: 'Drag & drop des widgets, redimensionnement, masquage et bouton Auto.', defaultOn: false, group: 'Tableau de bord' },
];

const GROUPS = ['Prospects', 'Import / Export', 'Tableau de bord'];

/** Valeur effective d'une fonctionnalité (defaultOn si non défini) */
function featureEnabled(cfg: UserFeaturesConfig | undefined, key: keyof UserFeaturesConfig, defaultOn: boolean): boolean {
  if (!cfg || cfg[key] === undefined) return defaultOn;
  return !!cfg[key];
}

const UserFeaturesTab: React.FC<UserFeaturesTabProps> = ({ company, onCompanyUpdate }) => {
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const cfg = company.userFeatures ?? {};

  const toggle = async (key: keyof UserFeaturesConfig, def: boolean) => {
    const current = featureEnabled(cfg, key, def);
    const newCfg: UserFeaturesConfig = { ...cfg, [key]: !current };
    setSaving(true);
    setMessage(null);
    try {
      const updated = await api.updateCompany(company.id, { userFeatures: newCfg } as any);
      onCompanyUpdate(updated);
      setMessage({ type: 'success', text: 'Paramètre sauvegardé.' });
      setTimeout(() => setMessage(null), 2000);
    } catch {
      setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-12 animate-in fade-in">
      <h3 className="text-2xl font-extrabold text-slate-900 mb-1 tracking-tight">
        Fonctionnalités des utilisateurs
      </h3>
      <p className="text-slate-500 text-sm font-medium mb-2">
        Activez ou désactivez les fonctionnalités accessibles aux utilisateurs (rôle <span className="font-black text-slate-900">USER</span>).
        Les administrateurs et managers conservent toujours l'accès complet.
      </p>
      <div className="flex items-center gap-2 mb-8">
        <span className="orion-warn-soft text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg">
          Affecte uniquement le rôle USER
        </span>
        {message && (
          <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg ${
            message.type === 'success'
              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
              : 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
          }`}>
            {message.text}
          </span>
        )}
      </div>

      <div className="space-y-8">
        {GROUPS.map(group => {
          const groupFeatures = FEATURES.filter(f => f.group === group);
          return (
            <div key={group} className="orion-settings-pref-card rounded-2xl p-6">
              <div className="text-[10px] font-black uppercase tracking-widest text-[#546a96] mb-4">{group}</div>
              <div className="space-y-3">
                {groupFeatures.map(f => {
                  const enabled = featureEnabled(cfg, f.key, f.defaultOn);
                  return (
                    <div key={f.key} className="flex items-center justify-between gap-4 py-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-bold text-slate-900">{f.label}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{f.description}</div>
                      </div>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => toggle(f.key, f.defaultOn)}
                        className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-transparent disabled:opacity-50 ${
                          enabled
                            ? 'orion-toggle-on-bg focus:ring-[rgba(17,197,214,0.5)]'
                            : 'orion-toggle-off-bg focus:ring-[rgba(100,140,210,0.4)]'
                        }`}
                        aria-label={`${enabled ? 'Désactiver' : 'Activer'} ${f.label}`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 p-4 rounded-2xl bg-[rgba(100,140,210,0.08)] border border-[rgba(100,140,210,0.15)]">
        <p className="text-[11px] text-[#7f93bc] leading-relaxed">
          <span className="font-black text-[#a8c0e8]">Note :</span> Les fonctionnalités désactivées sont uniquement masquées pour les utilisateurs.
          Les administrateurs (COMPANY_ADMIN) et managers (MANAGER) ont toujours accès à toutes les fonctionnalités.
        </p>
      </div>
    </div>
  );
};

export { featureEnabled };
export default UserFeaturesTab;
